// Import debug utility for runtime override support
import { debug } from '@/utils/debug';

// Campaign data freshness modes
export type CampaignDataFreshness = 'refresh_on_login' | 'refresh_on_first_nav' | 'refresh_always';

export const features = {
  // Debug features - completely removed in production builds
  showOptimizationDetails: import.meta.env.DEV || import.meta.env.VITE_SHOW_DEBUG === 'true',
  showCacheIndicators: import.meta.env.DEV,
  verboseApiLogging: import.meta.env.DEV,
  showPerformanceMetrics: import.meta.env.VITE_SHOW_PERFORMANCE === 'true',
  showDebugPanel: import.meta.env.DEV,

  // Production features with environment control
  enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  maintenanceMode: import.meta.env.VITE_MAINTENANCE_MODE === 'true',

  // Performance optimization features
  // When enabled, uses cached linked account data from login instead of re-checking on each page
  // Users must sign out/in if they unlink accounts via Google Ads console
  useLinkedAccountsCache: import.meta.env.VITE_USE_LINKED_ACCOUNTS_CACHE !== 'false', // Default: true

  // Campaign data freshness control
  // - 'refresh_on_login': Use cached app_configuration data, only refresh on login (fastest)
  // - 'refresh_on_first_nav': Fetch fresh data on first navigation, cache for session (balanced)
  // - 'refresh_always': Always fetch fresh data from database (slowest, most accurate)
  campaignDataFreshness: (import.meta.env.VITE_CAMPAIGN_DATA_FRESHNESS || 'refresh_on_login') as CampaignDataFreshness,

  // Data sync delay after asset creation (milliseconds)
  // Delay before triggering data pipeline after creating assets via Google Ads API
  // This gives Google Ads time to make the new asset available in their API
  // Default: 5000ms (5 seconds)
  dataSyncDelayMs: parseInt(import.meta.env.VITE_DATA_SYNC_DELAY_MS || '5000', 10),
};

// Type-safe feature checking (for boolean features only)
export function isFeatureEnabled(feature: Exclude<keyof typeof features, 'campaignDataFreshness' | 'dataSyncDelayMs'>): boolean {
  return features[feature] ?? false;
}

// Debug mode check with runtime override support
export function isDebugMode(): boolean {
  // Use the debug utility which respects runtime overrides
  // This allows __FLOURI_DEBUG__.toggle() to work
  const devMode = debug.isDev();

  // Also respect VITE_SHOW_DEBUG environment variable
  if (import.meta.env.VITE_SHOW_DEBUG === 'false') {
    return false;
  }

  return devMode;
}